import { Emitter } from '@wagmi/core/internal';
import { getDefaultConfig } from 'connectkit';
import {
  ENABLE_TESTNET,
  FORK_BASE_CHAIN_ID,
  FORK_CHAIN_ID,
  FORK_ENABLED,
  FORK_RPC_URL,
  networkConfigs,
} from 'src/utils/marketsAndNetworksConfig';
import { type Chain } from 'viem';
import { createConfig, CreateConfigParameters, http } from 'wagmi';
import { mainnet } from 'wagmi/chains';
import { injected, safe } from 'wagmi/connectors';

import { prodNetworkConfig, testnetConfig } from './networksConfig';

const testnetChains = Object.values(testnetConfig).map((config) => config.wagmiChain) as [
  Chain,
  ...Chain[]
];

let prodChains = Object.values(prodNetworkConfig).map((config) => config.wagmiChain) as [
  Chain,
  ...Chain[]
];

const { name, baseAssetDecimals, baseAssetSymbol } = networkConfigs[FORK_BASE_CHAIN_ID];

const forkChain: Chain = {
  id: FORK_CHAIN_ID,
  name: `${name} Fork`,
  nativeCurrency: {
    decimals: baseAssetDecimals,
    name: baseAssetSymbol,
    symbol: baseAssetSymbol,
  },
  rpcUrls: {
    default: { http: [FORK_RPC_URL] },
  },
  testnet: false,
};

if (FORK_ENABLED) {
  prodChains = [forkChain, ...prodChains];
}

const defaultConfig = {
  walletConnectProjectId: process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID as string,
  appName: 'Dinolend',
  appDescription: 'Dinolend V3 liquidity protocol',
  appUrl: 'https://dinolend.local',
  appIcon: '/dinolend-logo.svg',
};

const cypressConfig = createConfig(
  getDefaultConfig({
    chains: [forkChain],
    connectors: [injected()],
    ...defaultConfig,
  })
);

// Optional Ethereum mainnet RPC override. ConnectKit/wagmi resolve ENS names and
// avatars on mainnet; without an override viem uses its rate-limited public default
// (eth.merkle.io), which 429s / CORS-fails from the deployed origin. Point it at our
// own mainnet RPC instead.
const mainnetRpcOverride = (() => {
  const value = process.env.NEXT_PUBLIC_DINOLEND_MAINNET_RPC_URL?.replace(/\s+/g, '').trim();
  return value && /^https?:\/\//.test(value) ? value : undefined;
})();

const getTransport = (chainId: number) => {
  if (chainId === mainnet.id && mainnetRpcOverride) {
    return mainnetRpcOverride;
  }
  return networkConfigs[chainId]?.publicJsonRPCUrl[0];
};

const buildTransports = (chains: CreateConfigParameters['chains']) =>
  Object.fromEntries(chains.map((chain) => [chain.id, http(getTransport(chain.id))]));

const useTestnetChains =
  ENABLE_TESTNET ||
  process.env.NEXT_PUBLIC_ENV === 'staging' ||
  Boolean(process.env.NEXT_PUBLIC_DINOLEND_CHAIN_ID);
const baseChains = useTestnetChains ? testnetChains : prodChains;
// Ensure mainnet is in the chain list (with our transport) so ENS resolution uses the
// configured mainnet RPC even on a testnet-only deployment.
const configuredChains: [Chain, ...Chain[]] =
  mainnetRpcOverride && !baseChains.some((chain) => chain.id === mainnet.id)
    ? [mainnet, ...baseChains]
    : baseChains;

const prodCkConfig = getDefaultConfig({
  chains: configuredChains,
  transports: buildTransports(configuredChains),
  ...defaultConfig,
});

const familyConnectorId = 'familyAccountsProvider';

// Build the full connector set (including WalletConnect) only in the browser.
// During `next build` static page-data collection (and any SSR), instantiating
// the WalletConnect connector opens a relay socket that never settles, which makes
// Next's worker hang and fail with "page-data-collection-timeout". The wallet tree
// renders under <NoSsr>, so the server never needs the real connectors.
const buildProdConfig = () => {
  const connectorConfig = {
    chains: prodCkConfig.chains,
    emitter: new Emitter(''),
  };

  const connectors = prodCkConfig.connectors
    ?.map((connector) => {
      // initialize the connector with the emitter so we can access the id
      const c = connector(connectorConfig);
      if (c.id === 'safe') {
        return safe({
          allowedDomains: [/gnosis-safe.io$/, /app.safe.global$/, /dhedge.org$/],
        });
      } else {
        return connector;
      }
    })
    .sort((a, b) => {
      // sort connectors so the family connector is last
      // fixes slow wallet connections when running in the Safe UI
      if (a(connectorConfig).id === familyConnectorId) {
        return 1;
      }
      if (b(connectorConfig).id === familyConnectorId) {
        return -1;
      }
      return 0;
    });

  return createConfig({
    ...prodCkConfig,
    connectors,
  });
};

const prodConfig =
  typeof window === 'undefined'
    ? createConfig({ ...prodCkConfig, connectors: [injected()] })
    : buildProdConfig();

const isCypressEnabled = process.env.NEXT_PUBLIC_IS_CYPRESS_ENABLED === 'true';

export const wagmiConfig = isCypressEnabled ? cypressConfig : prodConfig;
